import { useMemo } from 'react'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { LineChart, LineSeries } from '../../../analytics/charts/LineChart'
import { ChartSkeleton } from '../common/Skeleton'
import { NoData } from 'src/components/analytics/common/NoData'

export default function ConversionRateComparison() {
  const {
    getters: { selectedTimeScale, refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.conversion_rate_comparison,
      isNoComparison: true,
      dependencies: {
        endpoints: ['/prefilled-address-timescale']
      }
    },
    refreshKey
  })

  const conversionRateData: Omit<LineSeries, 'selectedTimeScale'> & {
    nonGuestRate: number
    guestRate: number
  } = useMemo(() => {
    const rawData = data[0]?.data?.result?.widgetData?.data
    if (!rawData) {
      return {
        series: [],
        nonGuestRate: 0,
        guestRate: 0
      }
    }

    const nonGuestData = rawData?.non_guest?.data
    const guestData = rawData?.guest?.data

    const rateComaprisonAggregator = (data: any[]) => {
      const checkoutInitiated = data.reduce((acc, item) => {
        return acc + item.checkout_initiated
      }, 0)
      const orderCompleted = data.reduce((acc, item) => {
        return acc + item.order_completed
      }, 0)
      if (checkoutInitiated === 0) return 0
      return (orderCompleted / checkoutInitiated) * 100
    }

    let nonGuestRate = 0
    if (nonGuestData) {
      nonGuestRate = parseFloat(rateComaprisonAggregator(nonGuestData).toFixed(2))
    }
    let guestRate = 0
    if (guestData) {
      guestRate = parseFloat(rateComaprisonAggregator(guestData).toFixed(2))
    }

    const calculateValues = (orderCompleted: number, checkoutInitiated: number) => {
      if (checkoutInitiated === 0) return 0
      return parseFloat(((orderCompleted / checkoutInitiated) * 100).toFixed(2))
    }

    const series = [
      {
        label: 'Shopflo identified visitors',
        color: '#01CCA7',
        data:
          nonGuestData?.map((item: any) => ({
            x: item.date,
            y: calculateValues(item.order_completed, item.checkout_initiated)
          })) || []
      },
      {
        label: 'Others',
        color: '#CDD1D1',
        data:
          guestData?.map((item: any) => ({
            x: item.date,
            y: calculateValues(item.order_completed, item.checkout_initiated)
          })) || []
      }
    ]

    return {
      series: series.filter(series => series.data.length > 0),
      nonGuestRate,
      guestRate
    }
  }, [data])

  const nonGuestRate = conversionRateData.nonGuestRate
  const guestRate = conversionRateData.guestRate

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (conversionRateData.series.length > 0) {
      return (
        <>
          <div className='flex items-center gap-8 py-2'>
            <div className='flex gap-2 flex-col'>
              <div className='text-[18px] font-medium'>{nonGuestRate}%</div>
              <p className='text-coal-slate text-xs'>Sessions with address pre-filled</p>
            </div>
            <div className='text-coal-slate text-sm border-1 border-[#e9e9ea] p-1 h-fit w-fit rounded-full'>vs</div>
            <div className='flex gap-2 flex-col'>
              <div className='text-[18px] font-medium'>{guestRate}%</div>
              <p className='text-coal-slate text-xs'>Addresses not pre-filled</p>
            </div>
          </div>
          <div className='w-full h-[85%]'>
            <LineChart series={conversionRateData.series} isPercentage={true} selectedTimeScale={selectedTimeScale} />
          </div>
        </>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}
